import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import { resetAccountScopedStores } from "../../../app/auth/sessionStoreReset";
import { getProfile } from "../../../api/profile/getProfile";
import useAccountSettingsStore from "../../../features/settings/store/useAccountSettingsStore";
import { syncAccountProfileToShell } from "../../../features/settings/utils/accountShellState";
import {
  setHasMultipleAccounts,
  setSelectedAccountId as persistSelectedAccountId,
  setSelectedAccountSummary,
} from "../../../shared/lib/auth/accountContext";
import { clearListCache } from "../../../shared/lib/cache/listCache";
import { authRoutes } from "../config/authRoutes";
import { chooseAccount, getSelectableAccounts } from "../api/accountSelection";

export function useAccountSelection() {
  const navigate = useNavigate();
  const { updateAuthenticatedUser, user } = useAuth();
  const setAccountState = useAccountSettingsStore((state) => state.setAccountState);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [error, setError] = useState("");

  const hasAccounts = accounts.length > 0;
  const accountsLabel = useMemo(() => {
    const count = accounts.length || Number(user?.accountsCount || 0);
    return `${count} ${count === 1 ? "conta encontrada" : "contas encontradas"}`;
  }, [accounts.length, user?.accountsCount]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getSelectableAccounts();
      setAccounts(result.accounts);
      setHasMultipleAccounts(user?.email, result.accounts.length > 1);
    } catch (loadError) {
      setError(loadError?.message || "Não foi possível carregar suas contas.");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  async function selectAccount(accountId) {
    if (!accountId || refreshing) return;

    setSelectedAccountId(accountId);
    setRefreshing(true);
    setError("");

    try {
      let selectedAccount = accounts.find((account) => account.id === accountId);
      let selectedId = selectedAccount?.id || "";

      if (!selectedAccount) {
        const result = await chooseAccount(accountId);
        selectedId = result.selected_account_id;
        selectedAccount = result.selected_account;
      }

      if (!selectedId) {
        throw new Error("Contrato de seleção de conta inválido.");
      }

      clearListCache();
      resetAccountScopedStores();
      persistSelectedAccountId(user?.email, selectedId);
      setSelectedAccountSummary(user?.email, selectedAccount);
      setHasMultipleAccounts(user?.email, accounts.length > 1);

      let nextUser = updateAuthenticatedUser({
        ...user,
        accountId: selectedId,
        accountLogoUrl: selectedAccount?.logoUrl || user?.accountLogoUrl || null,
        accountName: selectedAccount?.name || user?.accountName || null,
        accountStatus: selectedAccount?.status || user?.accountStatus || null,
        account_status: selectedAccount?.status || user?.account_status || null,
        accountSelectionRequired: false,
        accountsCount: accounts.length || user?.accountsCount || 0,
        avatar_config: selectedAccount?.user?.avatarConfig || user?.avatar_config || null,
        is_account_owner: Boolean(selectedAccount?.isAccountOwner || selectedAccount?.is_account_owner),
        isAccountOwner: Boolean(selectedAccount?.isAccountOwner || selectedAccount?.is_account_owner),
        name: selectedAccount?.user?.displayName || user?.name || user?.username || null,
        nickname: selectedAccount?.user?.nickname ?? user?.nickname,
        permissions: selectedAccount?.permissions || user?.permissions || null,
        permissionsLoaded: Boolean(selectedAccount?.permissions || user?.permissionsLoaded),
        permission_levels:
          selectedAccount?.permissions?.permission_levels ||
          user?.permission_levels ||
          null,
        permission_slugs:
          selectedAccount?.permissions?.permission_slugs ||
          user?.permission_slugs ||
          null,
        picture: selectedAccount?.user?.pictureUrl || user?.picture || null,
        picture_url: selectedAccount?.user?.pictureUrl || user?.picture_url || null,
        user_avatar_url: selectedAccount?.user?.pictureUrl || user?.user_avatar_url || null,
      });

      getProfile()
        .then((profile) => {
          const hydratedUser = {
            ...(nextUser || user),
            accountId: selectedId,
            accountSelectionRequired: false,
          };
          syncAccountProfileToShell({
            profile,
            setAccountState,
            updateAuthenticatedUser,
            user: hydratedUser,
          });
        })
        .catch(() => {
          // A navegação não deve ser bloqueada se a hidratação do shell falhar.
        });

      /*
       * A seleção de conta já entrega o shell necessário para navegar. O perfil
       * completo é hidratado acima em segundo plano para não somar a latência do
       * Postgres ao primeiro paint da área privada.
       */
      if (!nextUser?.accountId || nextUser?.accountSelectionRequired) {
        throw new Error("Não foi possível confirmar a conta selecionada. Tente novamente.");
      }

      navigate(authRoutes.dashboard, { replace: true, state: { user: nextUser } });
    } catch (selectError) {
      setError(selectError?.message || "Não foi possível acessar esta conta.");
      setSelectedAccountId("");
    } finally {
      setRefreshing(false);
    }
  }

  return {
    accounts,
    accountsLabel,
    error,
    hasAccounts,
    loading,
    refreshing,
    reload: loadAccounts,
    selectAccount,
    selectedAccountId,
  };
}
